import api from '../../api/client';
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Typography,
  message,
  Modal,
  Space,
  Card,
  Table,
} from "antd";
import {
  ArrowLeftOutlined,
  BackwardOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import config from "./../../config";
import { usePermissions } from "../../permissions";
import { Link } from "react-router-dom";

const { Option } = Select;
const { Text } = Typography;

const EggCollectionPage = () => {
  const [form] = Form.useForm();
  const account = useSelector((state) => state.account);
  const [batchOptions, setBatchOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { createPermission } = usePermissions();

  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  // Fetch layer batches
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/animals/poultry/layers`);
        const data = response.data.data;
        const options = data.map((tag) => ({
          value: tag.batchId,
          label: `${tag.id} - ${tag.batchId} - ${tag.farm_name}`,
          farm: tag.farm,
          farmId: tag.farm_name,
        }));
        setBatchOptions(options);
        console.log(response.data, "from batch")
      } catch (error) {
        console.error("Error fetching batches:", error);
        message.error("Failed to load batches");
      } finally {
        setLoading(false);
      }
    };
    fetchBatches();
  }, [apiUrl]);

  // Disable future dates
  const disabledDate = (current) => {
    return current && current > moment().endOf("day");
  };

  // Calculate per-category totals and overall totals
  const calculateLineTotals = (lineValues) => {
    // Normal
    const normalTrays = lineValues.normal_trays || 0;
    const normalEggs = lineValues.normal_eggs || 0;
    const normalTotal = normalTrays * 30 + normalEggs;

    // Broken
    const brokenTrays = lineValues.broken_trays || 0;
    const brokenEggs = lineValues.broken_eggs || 0;
    const brokenTotal = brokenTrays * 30 + brokenEggs;

    // Medium
    const mediumTrays = lineValues.medium_trays || 0;
    const mediumEggs = lineValues.medium_eggs || 0;
    const mediumTotal = mediumTrays * 30 + mediumEggs;

    // Double yolk
    const doubleYolkTrays = lineValues.double_yolk_trays || 0;
    const doubleYolkEggs = lineValues.double_yolk_eggs || 0;
    const doubleYolkTotal = doubleYolkTrays * 30 + doubleYolkEggs;

    // Spoilt
    const spoiltTrays = lineValues.spoilt_trays || 0;
    const spoiltEggs = lineValues.spoilt_eggs || 0;
    const spoiltTotal = spoiltTrays * 30 + spoiltEggs;

    // Overall totals
    const totalEggs =
      normalTotal + brokenTotal + mediumTotal + doubleYolkTotal + spoiltTotal;
    const totalTrays = Math.floor(totalEggs / 30);
    const remainingEggs = totalEggs % 30;

    return {
      categories: [
        {
          name: "Normal",
          trays: normalTrays,
          eggs: normalEggs,
          total: normalTotal,
        },
        {
          name: "Broken",
          trays: brokenTrays,
          eggs: brokenEggs,
          total: brokenTotal,
        },
        {
          name: "Medium",
          trays: mediumTrays,
          eggs: mediumEggs,
          total: mediumTotal,
        },
        {
          name: "Double Yolk",
          trays: doubleYolkTrays,
          eggs: doubleYolkEggs,
          total: doubleYolkTotal,
        },
        {
          name: "Spoilt",
          trays: spoiltTrays,
          eggs: spoiltEggs,
          total: spoiltTotal,
        },
      ],
      totalEggs,
      totalTrays,
      remainingEggs,
    };
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const collections = values.collections.map((item) => {
        const totals = calculateLineTotals(item);
        return {
          // Basic info
          batch: item.batchId,
          recorded: item.recorded.format("YYYY-MM-DD"),
          remarks: item.remarks || "",
          userId: account.user.id,
          userName: account.user.firstname,

          // Per-category breakdown
          normal_trays: totals.categories[0].trays,
          normal_eggs: totals.categories[0].eggs,
          broken_trays: totals.categories[1].trays,
          broken_eggs: totals.categories[1].eggs,
          medium_trays: totals.categories[2].trays,
          medium_eggs: totals.categories[2].eggs,
          double_yolk_trays: totals.categories[3].trays,
          double_yolk_eggs: totals.categories[3].eggs,
          spoilt_trays: totals.categories[4].trays,
          spoilt_eggs: totals.categories[4].eggs,

          // Aggregated totals (for backward compatibility)
          collected: totals.totalEggs,
          spoilt: totals.categories[1].total, // broken total
          double_yolk: totals.categories[3].total,
          middle_sized: totals.categories[2].total,
          badEggs: totals.categories[4].total,
          numOfTrays: totals.totalTrays,
          remainingEggs: totals.remainingEggs,
        };
      });

      Modal.confirm({
        title: "Confirm Submission",
        content: `You are about to submit ${collections.length} collection line(s). Continue?`,
        onOk: async () => {
          setSubmitting(true);
          try {
            await api.post(`/eggs/update`, { collections });
            message.success("All collections submitted successfully.");
            form.resetFields();
          } catch (error) {
            console.error("Submission error:", error);
            const errorMsg =
              error.response?.data?.message || "An error occurred";
            message.error(errorMsg);
          } finally {
            setSubmitting(false);
          }
        },
      });
    } catch (error) {
}
  };

  // Category table columns for each line
  const categoryColumns = [
    {
      title: "Category",
      dataIndex: "name",
      key: "name",
      width: "25%",
    },
    {
      title: "Trays",
      dataIndex: "trays",
      key: "trays",
      width: "25%",
      render: (_, record) => (
        <Form.Item
          name={[record.fieldName, `${record.categoryKey}_trays`]}
          fieldKey={[record.fieldKey, `${record.categoryKey}_trays`]}
          noStyle
        >
          <InputNumber min={0} placeholder="Trays" style={{ width: "100%" }} />
        </Form.Item>
      ),
    },
    {
      title: "Remainder (Eggs)",
      dataIndex: "eggs",
      key: "eggs",
      width: "25%",
      render: (_, record) => (
        <Form.Item
          name={[record.fieldName, `${record.categoryKey}_eggs`]}
          fieldKey={[record.fieldKey, `${record.categoryKey}_eggs`]}
          noStyle
          rules={[{ type: "number", min: 0, max: 29, message: "0–29 only" }]}
        >
          <InputNumber
            min={0}
            max={29}
            placeholder="Eggs"
            style={{ width: "100%" }}
          />
        </Form.Item>
      ),
    },
    {
      title: "Total Eggs",
      dataIndex: "total",
      key: "total",
      width: "25%",
      render: (_, record, { form, lineName }) => {
        const values = form.getFieldsValue();
        const lineValues = values.collections?.[lineName] || {};
        const trays = lineValues[`${record.categoryKey}_trays`] || 0;
        const eggs = lineValues[`${record.categoryKey}_eggs`] || 0;
        const total = trays * 30 + eggs;
        return <Text>{total}</Text>;
      },
    },
  ];

  return (
    <>
      <Card
        title="Egg Collection (Per Category Row)"
        extra={
          <Link to="/eggs-update" className="mb-3 d-inline-block">
            <Button>
              <ArrowLeftOutlined /> Eggs Collections
            </Button>
          </Link>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            collections: [{}],
          }}
        >
          <Form.List name="collections">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, fieldKey, ...restField }) => (
                  <Card
                    key={key}
                    size="small"
                    title={`Collection Line ${name + 1}`}
                    extra={
                      fields.length > 1 && (
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          style={{ color: "red", fontSize: "16px" }}
                        />
                      )
                    }
                    style={{ marginBottom: 16 }}
                  >
                    {/* Date, Batch, Farm */}
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, "recorded"]}
                          fieldKey={[fieldKey, "recorded"]}
                          label="Date"
                          rules={[{ required: true, message: "Date required" }]}
                        >
                          <DatePicker
                            style={{ width: "100%" }}
                            format="YYYY-MM-DD"
                            disabledDate={disabledDate}
                            placeholder="Select date"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, "batchId"]}
                          fieldKey={[fieldKey, "batchId"]}
                          label="Batch No."
                          rules={[
                            { required: true, message: "Batch required" },
                          ]}
                        >
                          <Select
                            placeholder="-- Select batch --"
                            options={batchOptions}
                            loading={loading}
                            showSearch
                            optionFilterProp="label"
                            onChange={(value, option) => {
                              form.setFieldsValue({
                                collections: {
                                  [name]: { farm: option.farm_name },
                                },
                              });
                            }}
                          />
                        </Form.Item>
                      </Col>
                      {/* <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, "farm"]}
                          fieldKey={[fieldKey, "farm"]}
                          label="Farm"
                        >
                          <Input disabled />
                        </Form.Item>
                      </Col> */}
                    </Row>

                    {/* Category Table */}
                    <Table
                      sticky
                      dataSource={[
                        {
                          key: "normal",
                          name: "Normal",
                          categoryKey: "normal",
                          fieldName: name,
                          fieldKey: fieldKey,
                        },
                        {
                          key: "broken",
                          name: "Broken",
                          categoryKey: "broken",
                          fieldName: name,
                          fieldKey: fieldKey,
                        },
                        {
                          key: "medium",
                          name: "Medium",
                          categoryKey: "medium",
                          fieldName: name,
                          fieldKey: fieldKey,
                        },
                        {
                          key: "double_yolk",
                          name: "Double Yolk",
                          categoryKey: "double_yolk",
                          fieldName: name,
                          fieldKey: fieldKey,
                        },
                        {
                          key: "spoilt",
                          name: "Spoilt",
                          categoryKey: "spoilt",
                          fieldName: name,
                          fieldKey: fieldKey,
                        },
                      ]}
                      columns={categoryColumns.map((col) => ({
                        ...col,
                        render: (text, record) =>
                          col.render
                            ? col.render(text, record, { form, lineName: name })
                            : text,
                      }))}
                      pagination={false}
                      size="small"
                      bordered
                      style={{ marginBottom: 16 }}
                    />

                    {/* Overall Totals */}
                    {/* <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item label="Total Eggs">
                          <Text>
                            {(() => {
                              const values = form.getFieldsValue();
                              const lineValues =
                                values.collections?.[name] || {};
                              return calculateLineTotals(lineValues).totalEggs;
                            })()}
                          </Text>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label="Total Trays">
                          <Text>
                            {(() => {
                              const values = form.getFieldsValue();
                              const lineValues =
                                values.collections?.[name] || {};
                              return calculateLineTotals(lineValues).totalTrays;
                            })()}
                          </Text>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label="Remaining Eggs">
                          <Text>
                            {(() => {
                              const values = form.getFieldsValue();
                              const lineValues =
                                values.collections?.[name] || {};
                              return calculateLineTotals(lineValues)
                                .remainingEggs;
                            })()}
                          </Text>
                        </Form.Item>
                      </Col>
                    </Row> */}

                    <Form.Item
                      {...restField}
                      name={[name, "remarks"]}
                      fieldKey={[fieldKey, "remarks"]}
                      label="Remarks"
                    >
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  </Card>
                ))}

                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Collection Line
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting}
            >
              Submit All Collections
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </>
  );
};

export default EggCollectionPage;
